import React, { useState, useEffect, useContext } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import SideBar from "./SideBar";
import AuthContext from "./AuthContext";
import useIsMobile from "./hooks/useIsMobile";
import HeimdallPanel from "./components/HeimdallPanel";

// ── Barra de navegación inferior (26-ago, experiencia móvil "app nativa") ─────
// En el teléfono la navegación principal vive abajo, al alcance del pulgar:
// 4 destinos según rol + "Menú" que abre el drawer con todo lo demás.
//
// Cuáles son esos destinos lo decide el servidor (29-ago, SEGURIDAD #6): vienen
// en `descriptor.movil` desde backend/config/roles.js. Acá había una segunda
// lista de roles que había que acordarse de mantener igual a la del SideBar.

// ── Banner del modo "Ver como" (admin) ────────────────────────────────────────
// Barra ámbar inequívoca cuando el admin está mirando la app con el lente de
// otro rol. Empuja el contenido (no lo tapa) y ofrece la vuelta inmediata.
const ROL_VISTA_LABEL = { operator: "Taller", seller: "Ventas", accountant: "Contable" };

const BannerVistaComo = ({ mobile = false }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const vistaComo = auth?.vistaComo;
  const esAdminReal = (auth?.rolesReales || []).includes("admin");
  if (!vistaComo || !esAdminReal) return null;

  return (
    <div
      style={{
        width: "100%",
        flexShrink: 0,
        background: "#7a4a00",
        color: "#ffe9c7",
        borderBottom: "1px solid rgba(0,0,0,0.35)",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "7px 14px",
        paddingTop: mobile ? "calc(7px + env(safe-area-inset-top))" : 7,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.35,
        textAlign: "center",
      }}
    >
      <span>
        <i className="bi bi-eye" style={{ marginRight: 7 }} />
        Estás viendo la app como{" "}
        <b style={{ color: "#fff" }}>{ROL_VISTA_LABEL[vistaComo] || vistaComo}</b>
        {" "}— tu sesión sigue siendo admin.
      </span>
      <button
        type="button"
        onClick={() => {
          auth.setVistaComo(null);
          navigate("/");
        }}
        style={{
          background: "rgba(0,0,0,0.28)",
          color: "#fff",
          border: "1px solid rgba(255,233,199,0.55)",
          borderRadius: 8,
          padding: "3px 12px",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Volver a mi vista
      </button>
    </div>
  );
};

const BottomNav = ({ onMenu }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const items = auth?.descriptor?.movil || [];

  const Item = ({ icon, label, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="no-touch-min t-02"
      style={{
        flex: 1, background: "none", border: "none", cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        padding: "8px 0 6px", color: active ? "#e30613" : "#8a8a8a",
      }}
    >
      <i className={`bi ${icon}`} style={{ fontSize: 20, lineHeight: 1 }} />
      <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, letterSpacing: ".02em" }}>{label}</span>
    </button>
  );

  return (
    <nav style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1070,
      display: "flex", alignItems: "stretch",
      // Fondo opaco y sin backdrop-filter: en WebKit el blur sobre un
      // position:fixed hace que la barra se pinte en la posición vieja
      // durante la inercia/rebote del scroll. translateZ(0) la deja en su
      // propia capa de composición.
      background: "#1a1a1a",
      transform: "translateZ(0)", willChange: "transform",
      borderTop: "1px solid #2a2a2a",
      // Safe-area con piso fijo y alto mínimo estable (52px = alto de los
      // ítems + borde), para que el cambio del inset al scrollear no la
      // descoloque. Main reserva 64px + inset para el contenido.
      paddingBottom: "max(6px, env(safe-area-inset-bottom))",
      minHeight: "calc(52px + env(safe-area-inset-bottom))",
    }}>
      {items.map(i => (
        <Item key={i.path} icon={i.icono} label={i.etiqueta}
          active={location.pathname.startsWith(`/${i.path}`)}
          onClick={() => navigate(`/${i.path}`)} />
      ))}
      <Item icon="bi-grid-3x3-gap-fill" label="Menú" active={false} onClick={onMenu} />
    </nav>
  );
};

const Main = () => {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Auto-cerrar drawer al cambiar de ruta. Importante para mobile —
  // si no, el drawer queda abierto encima de la pantalla nueva.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Bloquear scroll del body cuando el drawer mobile está abierto, así no
  // hay scroll-bleed-through de la página por debajo del backdrop.
  useEffect(() => {
    if (isMobile && drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, drawerOpen]);

  // Nombre de la pantalla actual para el header móvil (pulido 26-ago):
  // se deriva de la ruta contra el menú — sin depender de document.title (no es
  // reactivo). Si la ruta no está en el menú, el header cae al branding de
  // siempre; eso ya pasaba con las pantallas sin ítem propio y ahora pasa además
  // mientras el descriptor viaja.
  const auth = useContext(AuthContext);
  const currentSeg = location.pathname.split("/")[1] || "";
  const screenName = (auth?.descriptor?.menu || []).find(i => i.path === currentSeg)?.nombre || null;

  if (isMobile) {
    // Estructura mobile — natural document flow:
    //   - Wrapper SIN height fijo (minHeight: 100%) → permite que el body/html
    //     scrollee naturalmente cuando el contenido excede el viewport.
    //   - Header con position: sticky top:0 → siempre visible al scrollear.
    //   - Outlet en flujo normal, su contenido determina la altura total.
    //   - Drawer y backdrop con position:fixed (out of flow).
    //
    // Por qué NO flex con `height:100% + overflow:auto en el outlet`:
    //   iOS Safari tiene un bug clásico donde flex:1 + overflow:auto en un
    //   parent de height:100% no permite scroll en algunos casos. La solución
    //   robusta es dejar que el body/html maneje el scroll natural.
    return (
      <div style={{ minHeight: "100%", overflowX: "clip" }}>
        {/* Banner "Ver como" + header comparten un solo wrapper sticky:
            así el banner empuja al header (no lo tapa) y ambos quedan
            siempre visibles al scrollear. */}
        <div style={{ position: "sticky", top: 0, zIndex: 1080 }}>
        <BannerVistaComo mobile />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            paddingTop: "calc(10px + env(safe-area-inset-top))",
            paddingBottom: 10,
            paddingLeft: "calc(12px + env(safe-area-inset-left))",
            paddingRight: "calc(12px + env(safe-area-inset-right))",
            background: "#1a1a1a",
            borderBottom: "1px solid #2a2a2a",
            minHeight: 60,
          }}
        >
          <button
            type="button"
            aria-label="Abrir menú"
            onClick={() => setDrawerOpen(true)}
            className="t-02 tap-card"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              border: "none",
              background: "#e30613",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <i className="bi bi-list" />
          </button>
          <img
            src={`${process.env.PUBLIC_URL}/logos/binimeliz-erp-icon.png`}
            alt="Binimeliz ERP"
            style={{ width: 38, height: 38, borderRadius: 10, flexShrink: 0 }}
          />
          {/* Título de la pantalla actual + marca como caption; si la ruta no
              está en el menú, se muestra el branding de siempre. */}
          {screenName ? (
            <div style={{ minWidth: 0 }}>
              <div style={{
                color: "#fff", fontWeight: 800, fontSize: 17, lineHeight: 1.15,
                letterSpacing: "-0.01em", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {screenName}
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", letterSpacing: ".05em", textTransform: "uppercase" }}>
                Binimeliz <span style={{ color: "#e30613" }}>ERP</span>
              </div>
            </div>
          ) : (
            <div
              style={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: ".02em",
              }}
            >
              Binimeliz <span style={{ color: "#e30613" }}>ERP</span>
            </div>
          )}
        </header>
        </div>

        {/* Backdrop — visible solo cuando drawer abierto */}
        {drawerOpen && (
          <div
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1090,
            }}
          />
        )}

        {/* SideBar drawer (position:fixed dentro del componente con prop mobile) */}
        <SideBar mobile open={drawerOpen} onItemClick={() => setDrawerOpen(false)} />

        {/* Contenido — flujo natural, body/html scrollean. El padding inferior
            deja espacio para la barra de navegación fija. */}
        <div style={{ paddingBottom: "calc(64px + env(safe-area-inset-bottom))" }}>
          <Outlet />
        </div>

        {/* Navegación inferior estilo app nativa */}
        <BottomNav onMenu={() => setDrawerOpen(true)} />

        {/* Heimdall: botón flotante por encima de la barra inferior */}
        <HeimdallPanel />
      </div>
    );
  }

  // Desktop: layout original (sidebar fija 60px + outlet)
  return (
    <div
      className="flex-row h-100"
      style={{ alignItems: "stretch", display: "flex" }}
    >
      <SideBar />
      <HeimdallPanel />
      {/* Columna de contenido: el banner "Ver como" va arriba del área de
          contenido (a la derecha de la sidebar fija, así no pelea con su
          position:fixed) y empuja el Outlet hacia abajo. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          width: 0,
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
        }}
      >
        <BannerVistaComo />
        <div
          className="flex-fill overflow-auto"
          style={{ minWidth: 0, display: "block", minHeight: 0 }}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Main;
